import { ApolloClient, NormalizedCacheObject } from '@apollo/client';

import { navigateTo } from '@occc-app/lib/router';
import { removeChainIdFromLocalStorage } from '@occc-app/Chain/utils';
import { snackbar } from '@occc-app/design-system';
import {
  MagicLinkExchangeRefreshTokenToAuthTokenDocument,
  MagicLinkExchangeRefreshTokenToAuthTokenMutation,
  MagicLinkExchangeRefreshTokenToAuthTokenMutationVariables,
} from '@occc/graphql';
import { clearCampaignComponentsFilters } from '@occc-app/routes/Campaign/CampaignDetailsPage/CampaignComponentsTable/campaignComponentsFiltersStorage';
import { clearTransactionSetSelection } from '@occc/feat-retailer-connect';
import { accessTokenVar, applyTokens, syncReactiveAuthVariables } from './authReactiveVariables';
import { tokenService, type AuthTokens } from './tokenService';

const REFRESH_SESSION_TOKEN_ERROR_MESSAGE = 'Failed to refresh session. Please log in again.';

export class AuthService {
  private apolloClient: ApolloClient<NormalizedCacheObject> | null = null;
  private disposeWsClientFn: (() => void) | null = null;

  public injectNetworkClient(client: ApolloClient<NormalizedCacheObject>) {
    this.apolloClient = client;
  }

  public injectDisposeWsClient(disposeFn: () => void) {
    this.disposeWsClientFn = disposeFn;
  }

  public setTokens(accessToken: string, refreshToken: string): void {
    applyTokens({ accessToken, refreshToken }, { persist: true });
  }

  public async refreshTokens(): Promise<void> {
    // Read from the shared store rather than the in-memory var: it is the
    // source of truth that another tab may have just rotated.
    const { accessToken, refreshToken } = tokenService.getTokens();

    if (!accessToken || !refreshToken || !this.apolloClient) {
      await this.clearSession();
      return;
    }

    try {
      const tokens = await this.exchangeRefreshToken(accessToken, refreshToken);
      this.setTokens(tokens.accessToken, tokens.refreshToken);
    } catch {
      await this.recoverFromFailedRefresh(refreshToken);
    }
  }

  public async clearSession(): Promise<void> {
    try {
      this.disposeWsClientFn?.();
    } catch {
      // Ignore disposal errors - cleanup should continue
    }
    this.setTokens('', '');

    await this.apolloClient?.clearStore();

    navigateTo('/');
    removeChainIdFromLocalStorage();
    clearCampaignComponentsFilters();
    clearTransactionSetSelection();
  }

  public getAccessToken(): string {
    return accessTokenVar();
  }

  // Exchanges the current tokens for a fresh pair. Throws if the mutation fails
  // or returns no tokens, so the caller can decide how to recover.
  private async exchangeRefreshToken(
    accessToken: string,
    refreshToken: string,
  ): Promise<AuthTokens> {
    if (!this.apolloClient) {
      throw new Error('AuthService network client not initialised');
    }

    const variables: MagicLinkExchangeRefreshTokenToAuthTokenMutationVariables = {
      input: { accessToken, refreshToken },
    };

    const response =
      await this.apolloClient.mutate<MagicLinkExchangeRefreshTokenToAuthTokenMutation>({
        variables,
        mutation: MagicLinkExchangeRefreshTokenToAuthTokenDocument,
      });

    const tokens = response.data?.magicLinkExchangeRefreshTokenToAuthToken;
    if (!tokens) {
      throw new Error('Refresh token exchange returned no tokens');
    }

    return { accessToken: tokens.accessToken, refreshToken: tokens.refreshToken };
  }

  // A refresh can fail simply because another tab already rotated the token we used.
  // On failure, let's just check to see if the token is different from the one we used.
  // If so, we use the new token and continue. If not, we clear the session and force a re-login.
  private async recoverFromFailedRefresh(usedRefreshToken: string): Promise<void> {
    const currentRefreshToken = tokenService.getRefreshToken();

    if (currentRefreshToken && currentRefreshToken !== usedRefreshToken) {
      syncReactiveAuthVariables();
      return;
    }

    snackbar.error(REFRESH_SESSION_TOKEN_ERROR_MESSAGE);
    await this.clearSession();
  }
}

export const authService = new AuthService();
